/**
 * Correção.
 *
 * Para N0-N2 é comparação com a chave — mas com diagnóstico: uma resposta errada
 * devolve O ERRO NOMEADO que ela reproduz, não apenas "errou". E, desde que cada
 * distrator carrega a conta que o produziu, devolve também a ANÁLISE: o caminho exato
 * que levou o aluno àquela resposta.
 *
 * Para N3-N4 é PREDICADO + ROBUSTEZ SOB PERTURBAÇÃO, que é a ideia central do projeto.
 * Ver `gradeConstruction` abaixo.
 */

import { hashOf } from '../core/hash.ts';
import { formatBR } from '../core/num.ts';
import { NOMES } from '../core/lexico.ts';
import {
  adjetivo,
  artigo,
  capitalizar,
  conta,
  de,
  em,
  lista,
  numeroDe,
  osN,
  umA,
  verbo,
} from '../core/pt.ts';
import { rngFromString } from '../core/rng.ts';
import {
  evaluate,
  type ConstructionSnapshot,
  type GeoId,
  type GeoObject,
} from '../geom/construction.ts';
import { movimentosEfetivos, replay, undoCount } from '../geom/tools.ts';
import { GRADE_TOL } from '../geom/tolerance.ts';
import type { Vec2 } from '../geom/vec.ts';
import { fromAngle, add } from '../geom/vec.ts';
import {
  familyOf,
  lookupError,
  CONSTR_ESTIMADO_A_OLHO,
  CONSTR_INCOMPLETO,
  ERRO_HERDADO,
  ERRO_PASSO_CERTO,
  EXPR_CONSTANTE,
} from '../schema/errors.ts';
import {
  avaliar,
  equivalentes,
  ErroExpressao,
  lerExpressao,
  usaVariavel,
  type Expr,
} from '../core/expressao.ts';
import type { PredicateOutcome, RubricOutcome } from '../schema/evidence.ts';
import type {
  AcaoBalanca,
  BalanceOpsInteraction,
  EstadoBalanca,
  ConstructInteraction,
  CoordinateEntryInteraction,
  DragPointsInteraction,
  ControlFitInteraction,
  RegionSelectInteraction,
  ErrorStepInteraction,
  ExpressionEntryInteraction,
  ItemInstance,
  OrderInteraction,
  NumericEntryInteraction,
  SelectOneInteraction,
} from '../schema/item.ts';
import type { Evidence, GradingResult, Response, ResponseConstruct } from '../schema/response.ts';
import { rt, txt, strong, type RichText } from '../schema/richtext.ts';
import type { ErrorCode } from '../schema/errors.ts';

export interface GradeContext {
  readonly latencyMs: number;
  readonly attemptIndex: number;
  /** Dicas graduadas pedidas antes de responder. */
  readonly dicasUsadas?: number;
}

export function grade(
  instance: ItemInstance,
  response: Response,
  ctx: GradeContext,
): GradingResult {
  if (response.kind === 'omitted') {
    return {
      correct: false,
      score: 0,
      evidence: baseEvidence(ctx, response),
      feedback: rt('Item não respondido.'),
    };
  }

  switch (instance.interaction.kind) {
    case 'select-one':
      return gradeSelectOne(instance.interaction, response, ctx);
    case 'numeric-entry':
      return gradeNumeric(instance.interaction, response, ctx);
    case 'construct':
      return gradeConstruction(instance.interaction, response, ctx);
    case 'coordinate-entry':
      return gradeCoordenadas(instance.interaction, response, ctx);
    case 'drag-points':
      return gradeArrastar(instance.interaction, response, ctx);
    case 'error-step':
      return gradePassoErrado(instance.interaction, response, ctx);
    case 'order':
      return gradeOrdem(instance.interaction, response, ctx);
    case 'expression-entry':
      return gradeExpressao(instance.interaction, response, ctx);
    case 'control-fit':
      return gradeAjuste(instance.interaction, response, ctx);
    case 'region-select':
      return gradeRegioes(instance.interaction, response, ctx);
    case 'balance-ops':
      return gradeBalanca(instance.interaction, response, ctx);
  }
}

/**
 * A BALANÇA. A resposta é CALIBRAR a balança: terminar com um recipiente sozinho num
 * prato, só pesos no outro — e o braço reto. O caminho é livre.
 *
 * Até 2026-09-22 a correção exigia também que saísse a mesma coisa dos dois lados, de
 * cada tipo de peça: tirar um recipiente de um prato e, do outro, os pesos que valem
 * o mesmo que ele era "tirou de um prato só", mesmo com a balança reta no fim. O autor
 * resolveu a questão exatamente assim e recebeu o erro: o item pede a calibragem, e
 * uma balança reta no fim É a calibragem. Quem tira peças até o braço ficar reto está
 * fazendo o que o item pede; o que ele não pode é parar com o braço torto.
 *
 * A economia de movimentos continua valendo estrelas, com a meta medida no caminho
 * mais curto que existe (`caminhoMaisCurto`), e não num caminho de livro.
 */
function gradeBalanca(
  inter: BalanceOpsInteraction,
  response: Response,
  ctx: GradeContext,
): GradingResult {
  if (response.kind !== 'balance-ops') return mismatch(ctx, response);
  const acoes = response.acoes;
  const estado = aplicarAcoes(inter.inicial, acoes);
  const base = { ...baseEvidence(ctx, response), actionCount: acoes.length };

  const rubric: RubricOutcome[] = (inter.alvo.partialCredit ?? []).map((c) => ({
    criterion: c.label,
    weight: c.weight,
    earned: safeTest(() => c.test(estado, acoes)) ? c.weight : 0,
  }));
  let analysis: RichText | undefined;
  try {
    analysis = inter.alvo.analyze?.(estado, acoes);
  } catch {
    analysis = undefined;
  }

  const reta = ladoQuePende(inter.inicial, estado) === 'nenhum';
  const chegou = sacoSozinho(estado) !== null;

  if (reta && chegou) {
    return {
      correct: true,
      score: 1,
      evidence: { ...base, rubric },
      feedback: rt(
        `Correto: a balança terminou equilibrada, com ${umA(inter.recipiente).adj('sozinho')} num prato e só pesos no outro.`,
      ),
      ...(analysis ? { analysis } : {}),
      estrelas: {
        obtidas: estrelasPorEconomia(acoes.length, inter.meta),
        movimentos: acoes.length,
        meta: inter.meta,
      },
    };
  }

  const diag = inter.alvo.diagnostics.find((d) => safeTest(() => d.test(estado, acoes)));
  const matchedError = diag?.error ?? null;
  return {
    correct: false,
    score: rubricScore(rubric),
    evidence: {
      ...base,
      matchedError,
      matchedErrorFamily: matchedError ? familyOf(matchedError) : null,
      rubric,
    },
    feedback: matchedError
      ? feedbackForError(matchedError)
      : rt(
          `Os pratos ainda não chegaram a ${umA(inter.recipiente).adj('sozinho')} de um lado e pesos do outro.`,
        ),
    ...(analysis ? { analysis } : {}),
  };
}

/**
 * Quantas partes iguais dá para fazer agora: o número de recipientes de um prato que
 * só tem recipientes, se esse número divide tudo o que há nos dois pratos.
 */
export function partesPossiveis(e: EstadoBalanca): number | null {
  const comSacos =
    e.esq.sacos > 0 && e.esq.pesos === 0
      ? e.esq
      : e.dir.sacos > 0 && e.dir.pesos === 0
        ? e.dir
        : null;
  if (!comSacos) return null;
  const k = comSacos.sacos;
  if (k < 2) return null;
  const tudo = [e.esq.sacos, e.esq.pesos, e.dir.sacos, e.dir.pesos];
  return tudo.every((n) => n % k === 0) ? k : null;
}

/**
 * REPARTIR SÓ COM O BRAÇO RETO. Repartir os dois pratos em partes iguais só preserva a
 * igualdade se ela existir. Com a balança torta o botão acendia, e o caminho de quem
 * esqueceu um lado — tirar 2 pesos da esquerda, repartir, e só então tirar 2 da
 * direita — fechava "2 de cada lado" com os pratos desiguais: depois de repartir, cada
 * peso vale o dobro dos de antes.
 */
export function repartirPossivel(inicial: EstadoBalanca, e: EstadoBalanca): number | null {
  return ladoQuePende(inicial, e) === 'nenhum' ? partesPossiveis(e) : null;
}

/** Uma ação que já se sabe legal: tirar uma peça que existe, repartir o que cabe. */
function passo(e: EstadoBalanca, a: AcaoBalanca): EstadoBalanca {
  if (a.t === 'repartir') {
    const k = a.partes;
    return {
      esq: { sacos: e.esq.sacos / k, pesos: e.esq.pesos / k },
      dir: { sacos: e.dir.sacos / k, pesos: e.dir.pesos / k },
    };
  }
  const p = e[a.prato];
  const novo = a.t === 'tirar-peso' ? { ...p, pesos: p.pesos - 1 } : { ...p, sacos: p.sacos - 1 };
  return a.prato === 'esq' ? { ...e, esq: novo } : { ...e, dir: novo };
}

/**
 * O estado depois de todas as ações. Ação que não caberia é ignorada: tirar peça de um
 * prato que não a tem, e repartir quando a interface não deixaria (`repartirPossivel`).
 */
export function aplicarAcoes(inicial: EstadoBalanca, acoes: readonly AcaoBalanca[]): EstadoBalanca {
  let e = inicial;
  for (const a of acoes) {
    const cabe =
      a.t === 'repartir'
        ? repartirPossivel(inicial, e) === a.partes
        : e[a.prato][a.t === 'tirar-peso' ? 'pesos' : 'sacos'] > 0;
    if (cabe) e = passo(e, a);
  }
  return e;
}

/**
 * PARA QUE LADO A BALANÇA PENDE, como uma balança de verdade: pelo PESO de cada prato.
 *
 * A interface decidia pela contagem de peças tiradas, sem distinguir o recipiente do peso
 * de 1 kg — e tirar um pote de um prato e um peso do outro deixava o braço reto, com um
 * lado x − 1 kg mais leve. O valor de x não precisa ser informado: a balança começa
 * equilibrada, e isso o determina. Com Δs e Δp as diferenças (esquerda − direita) de
 * recipientes e de pesos, o começo diz Δs₀·x + Δp₀ = 0; agora, a esquerda pesa
 * Δs·x + Δp a mais que a direita, e o sinal disso, multiplicado por Δs₀, é o de
 * Δp·Δs₀ − Δs·Δp₀ — conta de inteiros, sem arredondamento.
 */
export function ladoQuePende(inicial: EstadoBalanca, e: EstadoBalanca): 'esq' | 'dir' | 'nenhum' {
  const ds0 = inicial.esq.sacos - inicial.dir.sacos;
  const dp0 = inicial.esq.pesos - inicial.dir.pesos;
  const ds = e.esq.sacos - e.dir.sacos;
  const dp = e.esq.pesos - e.dir.pesos;
  // Recipientes iguais nos dois pratos desde o começo: x não se determina, e só os
  // pesos decidem enquanto os recipientes continuarem iguais.
  const esquerdaAMais = ds0 === 0 ? (ds === 0 ? dp : ds) : (dp * ds0 - ds * dp0) * Math.sign(ds0);
  return esquerdaAMais === 0 ? 'nenhum' : esquerdaAMais > 0 ? 'esq' : 'dir';
}

/**
 * O CAMINHO MAIS CURTO até a balança calibrada, por busca em largura sobre os estados.
 * É ele que dá a meta das estrelas: com o caminho livre, um caminho de livro — os pesos
 * aos pares, os recipientes aos pares, repartir — pode ser bem mais longo que o melhor.
 * Em 3x + 2 = x + 6 (x = 2) ele tem 7 movimentos, e o melhor tem 4: tirar o recipiente
 * da direita e os dois pesos da esquerda, que valem o mesmo, e repartir em 3.
 *
 * Os estados são poucos (no máximo 5 × 13 × 5 × 13), e a busca roda uma vez por variante.
 */
export function caminhoMaisCurto(inicial: EstadoBalanca): AcaoBalanca[] {
  const chave = (e: EstadoBalanca): string =>
    `${e.esq.sacos},${e.esq.pesos},${e.dir.sacos},${e.dir.pesos}`;
  const vindo = new Map<string, { readonly de: string; readonly acao: AcaoBalanca } | null>([
    [chave(inicial), null],
  ]);
  let fila: EstadoBalanca[] = [inicial];
  while (fila.length > 0) {
    const proxima: EstadoBalanca[] = [];
    for (const e of fila) {
      if (sacoSozinho(e) !== null && ladoQuePende(inicial, e) === 'nenhum') {
        const acoes: AcaoBalanca[] = [];
        for (let k = chave(e), v = vindo.get(k); v; k = v.de, v = vindo.get(k))
          acoes.unshift(v.acao);
        return acoes;
      }
      const possiveis: AcaoBalanca[] = [];
      for (const prato of ['esq', 'dir'] as const) {
        if (e[prato].sacos > 0) possiveis.push({ t: 'tirar-saco', prato });
        if (e[prato].pesos > 0) possiveis.push({ t: 'tirar-peso', prato });
      }
      const k = repartirPossivel(inicial, e);
      if (k !== null) possiveis.push({ t: 'repartir', partes: k });
      for (const acao of possiveis) {
        const depois = passo(e, acao);
        const c = chave(depois);
        if (vindo.has(c)) continue;
        vindo.set(c, { de: chave(e), acao });
        proxima.push(depois);
      }
    }
    fila = proxima;
  }
  return [];
}

/** O prato com exatamente um saco e nenhum peso, se houver — e o outro só com pesos. */
export function sacoSozinho(e: EstadoBalanca): 'esq' | 'dir' | null {
  const so = (p: { sacos: number; pesos: number }): boolean => p.sacos === 1 && p.pesos === 0;
  const pesosSo = (p: { sacos: number; pesos: number }): boolean => p.sacos === 0 && p.pesos > 0;
  if (so(e.esq) && pesosSo(e.dir)) return 'esq';
  if (so(e.dir) && pesosSo(e.esq)) return 'dir';
  return null;
}

/**
 * Regiões marcadas: comparação de CONJUNTOS. Um conjunto errado primeiro procura um erro
 * típico idêntico a ele; depois, as regras. A análise sempre diz quantas das regiões
 * pedidas foram marcadas, quais faltaram e quais sobraram.
 */
function gradeRegioes(
  inter: RegionSelectInteraction,
  response: Response,
  ctx: GradeContext,
): GradingResult {
  if (response.kind !== 'region-select') return mismatch(ctx, response);
  const base = baseEvidence(ctx, response);
  const sel = [...new Set(response.selecionadas)].sort();
  const chave = [...inter.chave].sort();
  const mesmo = (a: readonly string[], b: readonly string[]): boolean =>
    a.length === b.length && a.every((x, i) => x === b[i]);
  if (mesmo(sel, chave))
    return { correct: true, score: 1, evidence: base, feedback: rt('Correto.') };

  const hit = inter.erros.find((e) => mesmo([...e.conjunto].sort(), sel));
  const diag = hit ? undefined : inter.diagnosticos?.find((d) => safeTest(() => d.test(sel)));
  const matchedError = hit?.error ?? diag?.error ?? null;

  const rot = (id: string): string => inter.regioes.find((r) => r.id === id)?.rotulo ?? id;
  const faltaram = chave.filter((x) => !sel.includes(x));
  const sobraram = sel.filter((x) => !chave.includes(x));
  const g = NOMES[inter.oQue].g;
  const pedidas = osN(inter.oQue, chave.length).adj('pedido');
  const nSobra = numeroDe(sobraram.length);
  const contagem = txt(
    (sel.length === 0
      ? `Você não marcou ${adjetivo('nenhum', g, 'sg')} ${de(pedidas)}. `
      : `${capitalizar(de(pedidas))}, você marcou ${chave.length - faltaram.length}. `) +
      (faltaram.length > 0 && sel.length > 0
        ? `${capitalizar(verbo('faltar.pret', numeroDe(faltaram.length)))}: ${lista(faltaram.map(rot))}. `
        : '') +
      (sobraram.length > 0
        ? `Não ${verbo('dever.imperf', nSobra)} ser ${adjetivo('marcado', g, nSobra)}: ${lista(sobraram.map(rot))}.`
        : ''),
  );
  let analise: RichText | undefined;
  try {
    analise = hit?.explanation ?? inter.analisar?.(sel);
  } catch {
    analise = undefined;
  }
  return {
    correct: false,
    score: 0,
    evidence: {
      ...base,
      matchedError,
      matchedErrorFamily: matchedError ? familyOf(matchedError) : null,
    },
    feedback: matchedError
      ? feedbackForError(matchedError)
      : rt(
          `${capitalizar(osN(inter.oQue).adj('marcado'))} não são exatamente ${artigo(g, 'pl')} ${adjetivo('pedido', g, 'pl')}.`,
        ),
    analysis: analise ? [...analise, txt(' '), contagem] : [contagem],
  };
}

/** Ajustar vários controles: o mesmo esquema do arrastar — predicado, rubrica, diagnóstico. */
function gradeAjuste(
  inter: ControlFitInteraction,
  response: Response,
  ctx: GradeContext,
): GradingResult {
  if (response.kind !== 'control-fit') return mismatch(ctx, response);
  const v = response.valores;
  const base = { ...baseEvidence(ctx, response), actionCount: response.movimentos };
  const rubric: RubricOutcome[] = (inter.alvo.partialCredit ?? []).map((c) => ({
    criterion: c.label,
    weight: c.weight,
    earned: safeTest(() => c.test(v)) ? c.weight : 0,
  }));
  let analysis: RichText | undefined;
  try {
    analysis = inter.alvo.analyze?.(v);
  } catch {
    analysis = undefined;
  }
  if (safeTest(() => inter.alvo.correto(v))) {
    return {
      correct: true,
      score: 1,
      evidence: { ...base, rubric },
      feedback: rt('Correto.'),
      ...(analysis ? { analysis } : {}),
    };
  }
  const diag = inter.alvo.diagnostics.find((d) => safeTest(() => d.test(v)));
  const matchedError = diag?.error ?? null;
  return {
    correct: false,
    score: rubricScore(rubric),
    evidence: {
      ...base,
      matchedError,
      matchedErrorFamily: matchedError ? familyOf(matchedError) : null,
      rubric,
    },
    feedback: matchedError
      ? feedbackForError(matchedError)
      : rt('A figura ainda não ficou como o pedido.'),
    ...(analysis ? { analysis } : {}),
  };
}

/**
 * Expressão digitada. Três desfechos: ilegível (a mensagem diz o que não pôde ser
 * lido — não é um erro de matemática), equivalente à chave, ou equivalente a um erro
 * típico. Fora disso, a análise mostra, lado a lado, o que a expressão do aluno dá e o
 * que a tabela pede, nas primeiras posições.
 */
function gradeExpressao(
  inter: ExpressionEntryInteraction,
  response: Response,
  ctx: GradeContext,
): GradingResult {
  if (response.kind !== 'expression-entry') return mismatch(ctx, response);
  const base = baseEvidence(ctx, response);
  let e: Expr;
  try {
    e = lerExpressao(response.raw, inter.variavel);
  } catch (err) {
    return {
      correct: false,
      score: 0,
      evidence: base,
      feedback: rt('Não foi possível ler a expressão.'),
      analysis: [
        txt(err instanceof ErroExpressao ? `${err.message} ` : 'A expressão não pôde ser lida. '),
        txt(`Escreva, por exemplo, ${inter.exemplo}.`),
      ],
    };
  }
  const chave = lerExpressao(inter.chave, inter.variavel);
  if (equivalentes(e, chave, inter.amostras)) {
    return { correct: true, score: 1, evidence: base, feedback: rt('Correto.') };
  }
  const hit = inter.erros.find((x) => {
    try {
      return equivalentes(e, lerExpressao(x.expr, inter.variavel), inter.amostras);
    } catch {
      return false;
    }
  });
  const matchedError = hit?.error ?? (usaVariavel(e) ? null : EXPR_CONSTANTE);
  const vs = inter.amostras.slice(0, 3);
  const numBR = (v: number): string => formatBR(Math.round(v * 1000) / 1000);
  const comparacao = txt(
    `Para ${inter.variavel} = ${vs.map(numBR).join(', ')}, a sua expressão dá ${vs.map((x) => numBR(avaliar(e, x))).join(', ')}; ` +
      `a tabela pede ${vs.map((x) => numBR(avaliar(chave, x))).join(', ')}.`,
  );
  return {
    correct: false,
    score: 0,
    evidence: {
      ...base,
      matchedError,
      matchedErrorFamily: matchedError ? familyOf(matchedError) : null,
    },
    feedback: feedbackForError(matchedError),
    analysis: hit?.explanation ? [...hit.explanation, txt(' '), comparacao] : [comparacao],
  };
}

/**
 * Ordenar. Um erro típico é uma SEQUÊNCIA inteira — a que um raciocínio equivocado
 * produz. Quando a resposta não bate com nenhuma, a análise conta os pares fora de
 * ordem: "dois de dez" e "oito de dez" são respostas erradas muito diferentes.
 */
function gradeOrdem(inter: OrderInteraction, response: Response, ctx: GradeContext): GradingResult {
  if (response.kind !== 'order') return mismatch(ctx, response);
  const base = baseEvidence(ctx, response);
  const aluno = response.ordem;
  const igual = (a: readonly number[], b: readonly number[]): boolean =>
    a.length === b.length && a.every((x, i) => x === b[i]);
  if (igual(aluno, inter.ordem))
    return { correct: true, score: 1, evidence: base, feedback: rt('Correto.') };

  const posCerta = new Map(inter.ordem.map((item, i) => [item, i]));
  let fora = 0;
  for (let i = 0; i < aluno.length; i++) {
    for (let j = i + 1; j < aluno.length; j++) {
      if ((posCerta.get(aluno[i] as number) ?? 0) > (posCerta.get(aluno[j] as number) ?? 0)) fora++;
    }
  }
  const total = (aluno.length * (aluno.length - 1)) / 2;
  const hit = inter.erros.find((e) => igual(aluno, e.ordem));
  const matchedError = hit?.error ?? null;
  const contagem = txt(
    `Há ${conta(fora, 'par')} de cartões fora de ordem, de ${conta(total, 'par').adj('possivel')}.`,
  );
  return {
    correct: false,
    score: 0,
    evidence: {
      ...base,
      matchedError,
      matchedErrorFamily: matchedError ? familyOf(matchedError) : null,
    },
    feedback: feedbackForError(matchedError),
    analysis: hit?.explanation ? [...hit.explanation, txt(' '), contagem] : [contagem],
  };
}

/**
 * Encontre o erro. Errar para DEPOIS do passo certo e errar para ANTES são coisas
 * diferentes: depois, o aluno viu um valor errado mas não achou onde ele nasceu; antes,
 * ele desconfiou de um passo correto. Cada um tem o seu nome.
 */
function gradePassoErrado(
  inter: ErrorStepInteraction,
  response: Response,
  ctx: GradeContext,
): GradingResult {
  if (response.kind !== 'error-step') return mismatch(ctx, response);
  const base = baseEvidence(ctx, response);
  const n = response.passo + 1;
  const k = inter.passoErrado + 1;
  if (response.passo === inter.passoErrado) {
    return {
      correct: true,
      score: 1,
      evidence: base,
      feedback: rt('Correto.'),
      analysis: [txt(`O passo ${k} é, de fato, o primeiro passo errado. `), ...inter.explicacao],
    };
  }
  const matchedError = response.passo > inter.passoErrado ? ERRO_HERDADO : ERRO_PASSO_CERTO;
  const sobre: RichText =
    response.passo > inter.passoErrado
      ? [
          txt(
            `O passo ${n} tem um valor errado, mas só porque herdou o erro do passo ${k}: a partir do que veio antes, ` +
              `a conta dele está certa. O erro nasce no passo ${k}. `,
          ),
        ]
      : [txt(`O passo ${n} está certo. O primeiro erro aparece no passo ${k}. `)];
  return {
    correct: false,
    score: 0,
    evidence: { ...base, matchedError, matchedErrorFamily: familyOf(matchedError) },
    feedback: feedbackForError(matchedError),
    analysis: [...sobre, ...inter.explicacao],
  };
}

// ---------------------------------------------------------------------------
// N0-N2
// ---------------------------------------------------------------------------

function gradeSelectOne(
  inter: SelectOneInteraction,
  response: Response,
  ctx: GradeContext,
): GradingResult {
  if (response.kind !== 'select-one') return mismatch(ctx, response);

  const escolhida = inter.alternatives.find((a) => a.id === response.choiceId);
  const correct = response.choiceId === inter.keyId;
  const matchedError = escolhida?.error ?? null;
  // A análise é a conta que produziu ESTA alternativa. Só faz sentido quando o aluno
  // errou: a conta da chave é a resolução, e ela já vem no item.
  const analysis = correct ? undefined : escolhida?.explanation;

  return {
    correct,
    score: correct ? 1 : 0,
    evidence: {
      ...baseEvidence(ctx, response),
      matchedError,
      matchedErrorFamily: matchedError ? familyOf(matchedError) : null,
    },
    feedback: correct ? rt('Correto.') : feedbackForError(matchedError),
    ...(analysis ? { analysis } : {}),
  };
}

function gradeNumeric(
  inter: NumericEntryInteraction,
  response: Response,
  ctx: GradeContext,
): GradingResult {
  if (response.kind !== 'numeric-entry') return mismatch(ctx, response);

  const v = response.value;
  if (v === null || !Number.isFinite(v)) {
    return {
      correct: false,
      score: 0,
      evidence: baseEvidence(ctx, response),
      feedback: rt('Resposta não reconhecida como número.'),
    };
  }

  // Com FAIXA, qualquer valor dentro dela está certo; sem faixa, só a chave.
  const correct = inter.accept
    ? v >= inter.accept.lo - inter.absTol && v <= inter.accept.hi + inter.absTol
    : Math.abs(v - inter.keyValue) <= inter.absTol;

  // Mesmo digitada, uma resposta errada recebe diagnóstico: comparamos com as saídas
  // dos solucionadores defeituosos — e, se nenhuma casar, com as faixas típicas de erro.
  let matchedError: ErrorCode | null = null;
  let analysis: RichText | undefined;
  if (!correct) {
    const hit = inter.errorValues.find((e) => Math.abs(v - e.value) <= inter.absTol);
    const faixa = hit
      ? undefined
      : inter.errorIntervals?.find((e) => v >= e.lo - inter.absTol && v <= e.hi + inter.absTol);
    matchedError = hit?.error ?? faixa?.error ?? null;
    analysis = hit?.explanation ?? faixa?.explanation ?? semDiagnostico(v, inter);
  }

  return {
    correct,
    score: correct ? 1 : 0,
    evidence: {
      ...baseEvidence(ctx, response),
      matchedError,
      matchedErrorFamily: matchedError ? familyOf(matchedError) : null,
    },
    feedback: correct ? rt('Correto.') : feedbackForError(matchedError),
    ...(analysis ? { analysis } : {}),
  };
}

/**
 * Uma resposta digitada que não casa com nenhum erro conhecido.
 *
 * Dizer isso com todas as letras é melhor do que inventar um diagnóstico: o aluno
 * pode ter errado uma conta de outro jeito, e o que se tem de honesto a oferecer é a
 * comparação com a resolução.
 */
// ---------------------------------------------------------------------------
// Coordenadas digitadas
// ---------------------------------------------------------------------------

const mesmoPonto = (a: Vec2, b: Vec2): boolean =>
  Math.abs(a.x - b.x) < 1e-9 && Math.abs(a.y - b.y) < 1e-9;

/** Os dois conjuntos de pontos são iguais, sem importar a ordem? */
function mesmoConjunto(a: readonly Vec2[], b: readonly Vec2[]): boolean {
  if (a.length !== b.length) return false;
  const usados = new Set<number>();
  return a.every((p) => {
    const i = b.findIndex((q, k) => !usados.has(k) && mesmoPonto(p, q));
    if (i < 0) return false;
    usados.add(i);
    return true;
  });
}

const parTxt = (p: Vec2): string => `(${formatBR(p.x)}, ${formatBR(p.y)})`;

/**
 * Coordenadas digitadas numa ficha. Os pontos pedidos valem como CONJUNTO: num
 * quadrado ABCD com A e C dados, escrever o B no lugar do D descreve o mesmo quadrado,
 * percorrido no outro sentido — e não é erro.
 */
function gradeCoordenadas(
  inter: CoordinateEntryInteraction,
  response: Response,
  ctx: GradeContext,
): GradingResult {
  if (response.kind !== 'coordinate-entry') return mismatch(ctx, response);
  const base = baseEvidence(ctx, response);
  const nomes = inter.pontos.filter((p) => !p.valor).map((p) => p.nome);

  if (response.valores.some((v) => v === null)) {
    return {
      correct: false,
      score: 0,
      evidence: base,
      feedback: rt('Resposta incompleta: há coordenadas em branco ou que não são números.'),
      analysis: [txt('Cada ponto pedido precisa das duas coordenadas, escritas como números.')],
    };
  }

  const pts = response.valores as readonly Vec2[];
  const correct = inter.respostas.some((r) => mesmoConjunto(pts, r));
  const escritos = nomes.map((n, i) => `${n} = ${parTxt(pts[i] as Vec2)}`).join(' e ');

  if (correct) {
    return {
      correct: true,
      score: 1,
      evidence: base,
      feedback: rt('Correto.'),
      analysis: [txt(`Você escreveu ${escritos}.`)],
    };
  }

  const hit = inter.erros.find((e) => mesmoConjunto(pts, e.pontos));
  const matchedError = hit?.error ?? null;
  let analysis: RichText;
  if (hit?.explanation) {
    analysis = hit.explanation;
  } else {
    const certa = inter.respostas[0] ?? [];
    const certos = pts.filter((p) => certa.some((q) => mesmoPonto(p, q))).length;
    analysis = [
      txt(`Você escreveu ${escritos}. `),
      txt(
        certos === 0
          ? 'Nenhum dos pontos está no lugar certo, e a resposta não corresponde a nenhum dos erros típicos que este item reconhece. '
          : // "Os pontos estão certos, mas não os 3" — com 2 certos de 3 — dizia o contrário
            // do que queria. Agora diz quantos: "2 dos pontos estão certos, mas não os 3".
            `${certos === 1 ? 'Um' : certos} dos pontos ${verbo('estar.pres', numeroDe(certos))} ${adjetivo('certo', 'm', numeroDe(certos))}, mas não os ${nomes.length}. `,
      ),
      txt('Compare o seu caminho com a resolução.'),
    ];
  }
  return {
    correct: false,
    score: 0,
    evidence: {
      ...base,
      matchedError,
      matchedErrorFamily: matchedError ? familyOf(matchedError) : null,
    },
    feedback: feedbackForError(matchedError),
    analysis,
  };
}

// ---------------------------------------------------------------------------
// Pontos arrastados
// ---------------------------------------------------------------------------

function gradeArrastar(
  inter: DragPointsInteraction,
  response: Response,
  ctx: GradeContext,
): GradingResult {
  if (response.kind !== 'drag-points') return mismatch(ctx, response);
  const pos = response.posicoes;
  const base = { ...baseEvidence(ctx, response), actionCount: response.movimentos };
  const rubric: RubricOutcome[] = (inter.alvo.partialCredit ?? []).map((c) => ({
    criterion: c.label,
    weight: c.weight,
    earned: safeTest(() => c.test(pos)) ? c.weight : 0,
  }));
  let analysis: RichText | undefined;
  try {
    analysis = inter.alvo.analyze?.(pos);
  } catch {
    analysis = undefined;
  }

  if (safeTest(() => inter.alvo.correto(pos))) {
    return {
      correct: true,
      score: 1,
      evidence: { ...base, rubric },
      feedback: rt('Correto.'),
      ...(analysis ? { analysis } : {}),
    };
  }

  const diag = inter.alvo.diagnostics.find((d) => safeTest(() => d.test(pos)));
  const matchedError = diag?.error ?? null;
  return {
    correct: false,
    score: rubricScore(rubric),
    evidence: {
      ...base,
      matchedError,
      matchedErrorFamily: matchedError ? familyOf(matchedError) : null,
      rubric,
    },
    feedback: matchedError
      ? feedbackForError(matchedError)
      : rt(
          inter.desenho === 'barras'
            ? 'As colunas ainda não estão nas alturas pedidas.'
            : inter.desenho === 'marcadores'
              ? 'Os marcadores ainda não estão nos pontos pedidos.'
              : 'Os pontos ainda não estão nas posições pedidas.',
        ),
    ...(analysis ? { analysis } : {}),
  };
}

function semDiagnostico(v: number, inter: NumericEntryInteraction): RichText {
  const unidade = inter.unit ? ` ${inter.unit}` : '';
  const verbo = inter.viaControl ? 'escolheu' : 'digitou';
  return [
    txt(
      `O valor que você ${verbo}, ${formatBR(v, inter.decimals)}${unidade}, não coincide com a ` +
        'resposta certa nem com nenhum dos erros típicos que este item sabe reconhecer. ',
    ),
    txt('Compare a sua conta, passo a passo, com a resolução.'),
  ];
}

// ---------------------------------------------------------------------------
// N3-N4: predicado + robustez
// ---------------------------------------------------------------------------

/**
 * A correção que aceita qualquer caminho válido E reprova o "no olho".
 *
 * 1. REEXECUTA A TRAJETÓRIA sobre os dados do item. O snapshot que veio do cliente é
 *    só cache e não é confiável; a trajetória é a resposta de verdade.
 *
 * 2. AVALIA O PREDICADO SEMÂNTICO. Não há gabarito: não se compara a construção do
 *    aluno com uma construção abençoada, verifica-se uma propriedade geométrica.
 *    Dois alunos, um pelo método dos dois arcos e outro pelo losango, produzem DAGs
 *    completamente diferentes e ambos passam. O corretor nunca olha o caminho.
 *
 * 3. PERTURBA E REAVALIA. Move os dados perturbáveis dentro de um raio e reavalia o
 *    DAG DO PRÓPRIO ALUNO. Todas as tentativas precisam passar.
 *
 *    É o passo 3 que faz o passo 2 poder ser generoso. Quem arrastou um ponto solto
 *    até PARECER a bissetriz passa no passo 2 — e falha no 3, porque quando os lados
 *    do ângulo se movem o ponto solto fica parado. Quem construiu com arcos tem tudo
 *    derivado dos dados: a construção se move junto e o predicado continua valendo.
 *
 *    É assim que o Euclidea e a verificação automática do GeoGebra funcionam, e é a
 *    razão pela qual o snapping precisava registrar proveniência honesta.
 */
export function gradeConstruction(
  inter: ConstructInteraction,
  response: Response,
  ctx: GradeContext,
): GradingResult {
  if (response.kind !== 'construct') return mismatch(ctx, response);
  const r = response as ResponseConstruct;

  const fingerprint = hashOf(r.trajectory);
  const base = {
    ...baseEvidence(ctx, response),
    actionCount: movimentosEfetivos(inter.givens, r.trajectory),
    undoCount: undoCount(r.trajectory),
    responseFingerprint: fingerprint,
  };

  // (1) reexecuta a trajetória
  const snapshot = replay(inter.givens, r.trajectory);
  const evaluated = evaluate(snapshot);
  const givensEval = evaluate(inter.givens);

  // A análise desta resposta, quando o item sabe fazê-la. Uma análise que lança não
  // derruba a correção: o veredito não depende dela.
  const analisar = (): RichText | undefined => {
    const fn = inter.target.analyze;
    if (!fn) return undefined;
    try {
      return fn(evaluated.objects, givensEval.objects, GRADE_TOL);
    } catch {
      return undefined;
    }
  };

  if (r.trajectory.length === 0) {
    const analysis = analisar();
    // Mesmo sem nada construído, o diagnóstico é o DO ITEM: numa malha, "faltam pontos";
    // numa construção, "incompleta". Um rótulo genérico de construção diria a quem não
    // marcou ponto nenhum que ele "fez os passos auxiliares".
    const diag = inter.target.diagnostics.find((d) =>
      safeTest(() => d.test(evaluated.objects, givensEval.objects, GRADE_TOL)),
    );
    const matchedError = diag?.error ?? CONSTR_INCOMPLETO;
    return {
      correct: false,
      score: 0,
      evidence: {
        ...base,
        matchedError,
        matchedErrorFamily: familyOf(matchedError),
        predicates: [],
        robust: null,
      },
      feedback: rt('Nada foi construído.'),
      ...(analysis ? { analysis } : {}),
    };
  }

  // (2) predicado semântico
  const main = inter.target.predicate(evaluated.objects, givensEval.objects, GRADE_TOL);
  const predicates: PredicateOutcome[] = [main];

  const rubric = runRubric(inter, evaluated.objects, givensEval.objects);

  if (!main.ok) {
    const diag = inter.target.diagnostics.find((d) =>
      safeTest(() => d.test(evaluated.objects, givensEval.objects, GRADE_TOL)),
    );
    const matchedError = diag?.error ?? null;
    const partial = rubricScore(rubric);
    // Sem análise própria do item, a mensagem do predicado já diz algo concreto:
    // "tem 1 ângulo reto; um trapézio retângulo precisa de 2".
    const analysis = analisar() ?? main.message;

    return {
      correct: false,
      score: partial,
      evidence: {
        ...base,
        matchedError,
        matchedErrorFamily: matchedError ? familyOf(matchedError) : null,
        predicates,
        robust: null,
        rubric,
      },
      feedback: matchedError ? feedbackForError(matchedError) : main.message,
      analysis,
    };
  }

  // (3) robustez sob perturbação
  const rob = checkRobustness(inter, snapshot, fingerprint);
  predicates.push(rob.outcome);

  if (!rob.robust) {
    return {
      correct: false,
      score: Math.min(rubricScore(rubric), 0.5),
      evidence: {
        ...base,
        matchedError: CONSTR_ESTIMADO_A_OLHO,
        matchedErrorFamily: familyOf(CONSTR_ESTIMADO_A_OLHO),
        predicates,
        robust: false,
        rubric,
      },
      feedback: [
        txt('A figura '),
        strong('parece'),
        txt(
          ' certa, mas não foi construída: quando os dados do problema se movem, ela deixa ' +
            'de valer. Use as ferramentas para derivar o objeto dos dados, em vez de ' +
            'posicioná-lo a olho.',
        ),
      ],
      analysis: analisar() ?? rob.outcome.message,
    };
  }

  const analysis = analisar();
  return {
    correct: true,
    score: 1,
    evidence: {
      ...base,
      matchedError: null,
      matchedErrorFamily: null,
      predicates,
      robust: true,
      rubric,
    },
    feedback: rt('Construção correta — e ela se mantém quando a figura se move.'),
    ...(analysis ? { analysis } : {}),
    ...(inter.meta
      ? {
          estrelas: {
            obtidas: estrelasPorEconomia(base.actionCount, inter.meta),
            movimentos: base.actionCount,
            meta: inter.meta,
          },
        }
      : {}),
  };
}

/**
 * Estrelas por economia: três com a meta ou menos, duas com até dois movimentos a mais,
 * uma além disso. Desfazer não conta movimento (ver `moveCount`) — errar e voltar atrás
 * não custa estrela; o que custa é deixar objetos a mais na construção.
 */
export function estrelasPorEconomia(movimentos: number, meta: number): 1 | 2 | 3 {
  if (movimentos <= meta) return 3;
  if (movimentos <= meta + 2) return 2;
  return 1;
}

interface RobustnessResult {
  readonly robust: boolean;
  readonly outcome: PredicateOutcome;
}

function checkRobustness(
  inter: ConstructInteraction,
  studentSnapshot: ConstructionSnapshot,
  fingerprint: string,
): RobustnessResult {
  const trials = inter.target.robustnessTrials;
  if (trials <= 0 || inter.perturbable.length === 0) {
    return {
      robust: true,
      outcome: {
        name: 'robustness',
        ok: true,
        residual: 0,
        unit: 'count',
        tolerance: 0,
        message: rt('Sem checagem de robustez para este item.'),
      },
    };
  }

  // Semeado pela impressão digital da resposta: recorrigir o mesmo trabalho dá
  // exatamente o mesmo veredito, hoje e daqui a um ano.
  const rng = rngFromString(`robustness:${fingerprint}`);

  let passaram = 0;
  let validas = 0;
  let piorResiduo = 0;
  const maxTentativas = trials * 5;

  for (let t = 0; t < maxTentativas && validas < trials; t++) {
    const sub = rng.fork(`trial:${t}`);
    const override: Record<string, Vec2> = {};

    for (const p of inter.perturbable) {
      const original = inter.givens.givenPositions[p.id as string];
      if (!original) continue;
      const ang = sub.float(0, 2 * Math.PI);
      const raio = p.radius * Math.sqrt(sub.next()); // uniforme no disco
      override[p.id as string] = add(original, fromAngle(ang, raio));
    }

    const perturbedGivens = evaluate(inter.givens, override);
    // Uma perturbação pode degenerar legitimamente a configuração do PROBLEMA.
    // Nesse caso a tentativa é descartada e reamostrada, não contada como falha.
    if (perturbedGivens.degenerate.length > 0) continue;

    const perturbedStudent = evaluate(studentSnapshot, override);
    validas++;

    const out = inter.target.predicate(
      perturbedStudent.objects,
      perturbedGivens.objects,
      GRADE_TOL,
    );
    if (out.ok) passaram++;
    else piorResiduo = Math.max(piorResiduo, Number.isFinite(out.residual) ? out.residual : 1e6);
  }

  const robust = validas > 0 && passaram === validas;

  return {
    robust,
    outcome: {
      name: 'robustness',
      ok: robust,
      residual: validas - passaram,
      unit: 'count',
      tolerance: 0,
      message: rt(
        robust
          ? `A construção continuou valendo ${em(osN('vez', validas))} em que os dados do problema foram movidos.`
          : `Os dados do problema foram movidos ${conta(validas, 'vez')}, e em ${validas - passaram} delas ` +
              'a construção deixou de valer — sinal de que alguma parte foi posicionada a olho.',
      ),
    },
  };
}

function runRubric(
  inter: ConstructInteraction,
  objs: ReadonlyMap<GeoId, GeoObject>,
  givens: ReadonlyMap<GeoId, GeoObject>,
): RubricOutcome[] {
  const criteria = inter.target.partialCredit;
  if (!criteria) return [];
  return criteria.map((c) => ({
    criterion: c.label,
    weight: c.weight,
    earned: safeTest(() => c.test(objs, givens, GRADE_TOL)) ? c.weight : 0,
  }));
}

function rubricScore(rubric: readonly RubricOutcome[]): number {
  if (rubric.length === 0) return 0;
  const total = rubric.reduce((s, r) => s + r.weight, 0);
  if (total <= 0) return 0;
  return rubric.reduce((s, r) => s + r.earned, 0) / total;
}

function safeTest(fn: () => boolean): boolean {
  try {
    return fn();
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Comuns
// ---------------------------------------------------------------------------

function baseEvidence(ctx: GradeContext, response: Response): Evidence {
  return {
    matchedError: null,
    matchedErrorFamily: null,
    predicates: [],
    robust: null,
    rubric: [],
    latencyMs: ctx.latencyMs,
    attemptIndex: ctx.attemptIndex,
    actionCount: 0,
    undoCount: 0,
    responseFingerprint: hashOf(response),
    ...(ctx.dicasUsadas ? { dicasUsadas: ctx.dicasUsadas } : {}),
  };
}

function mismatch(ctx: GradeContext, response: Response): GradingResult {
  return {
    correct: false,
    score: 0,
    evidence: baseEvidence(ctx, response),
    feedback: rt('Formato de resposta incompatível com este item.'),
  };
}

function feedbackForError(code: ErrorCode | null): RichText {
  if (!code) return rt('Resposta incorreta.');
  const a = lookupError(code);
  return [strong(a.label), txt(' — '), txt(a.description)];
}
